import React from 'react';
import {
	Button,
	Dropdown,
	Icon,
	LabelLawyerState,
	SolidDot,
	Typography,
} from '@/components/common';
import { CLOSED_STATUSES_CASE, isTerminalCaseStatus } from '@/const/cases';
import Icons from '@/const/icons';
import { CaseStatusFilter, Option, StatusColorCase } from '@/interfaces';
import clsx from 'clsx';
import { ErrorIcon } from '@/components/dashboard';
import { useRouter } from 'next/router';
import AppRoutes from '@/const/routes';
import { format } from 'date-fns';
import { casesLawyerService, GET_LAWYER_CASES_LIST } from '@/api';
import { formatErrorMessage } from '@/lib';
import { useQueryClient } from '@tanstack/react-query';
import { useEndOrDeleteCase } from '@/hooks';
import {
	END_OR_DELETE_CASE_MODAL_SHELL,
	EndOrDeleteCaseModal,
	EndOrDeleteCaseModalTitle,
} from './case-details-layout/EndOrDeleteCaseModal';

interface Props {
	onClickPin: () => void;
	caseId: string;
	step: CaseStatusFilter;
	isDraft: boolean;
	statusColor: StatusColorCase;
	isPinned: boolean;
	title: string;
	lawyerTasks: number;
	showPin: boolean;
	hasError: boolean;
	createdAt?: string;
	totalClients: number;
	totalMessages: number;
	isClosedToNewClients?: boolean;
	isHideCase?: boolean;
	isArchived?: boolean;
	isArchivePending?: boolean;
	onArchive?: (caseId: string) => void;
	onUnarchive?: (caseId: string) => void;
	progressName?: CaseStatusFilter;
	getCurrentPhase: (
		id: string
	) => Promise<{ href: string; name: string } | null>;
}

export const CaseCard: React.FC<Props> = ({
	isPinned,
	caseId,
	step,
	isDraft,
	title,
	onClickPin,
	lawyerTasks,
	statusColor,
	showPin,
	hasError,
	createdAt,
	totalClients,
	totalMessages,
	isClosedToNewClients,
	getCurrentPhase,
	isHideCase,
	isArchived,
	isArchivePending,
	onArchive,
	onUnarchive,
	progressName,
}) => {
	const router = useRouter();
	const queryClient = useQueryClient();
	const {
		hasClients,
		isEnded,
		isDisabled: isEndOrDeleteDisabled,
		isSubmitting,
		isOpen: isConfirmOpen,
		Modal: ModalConfirmation,
		register: registerEndOrDelete,
		open: openEndOrDeleteModal,
		close: closeEndOrDeleteModal,
		confirm: handleEndOrDelete,
		caseTitle,
	} = useEndOrDeleteCase({
		caseId,
		caseTitle: title,
		clientCount: totalClients ?? 0,
		progressName: progressName ?? step,
	});

	const handleClick = async () => {
		const response = await getCurrentPhase(caseId);
		const caseRoutesValid = [
			'investigation',
			'applicant-intake',
			'active-case',
			'settlement',
		];

		let href = response?.href || '';
		// fix to the href would be case-closed
		const segments = href.split('/').filter(Boolean);
		const segmentToValidate = segments[segments.length - 1] || '';

		if (!isDraft) {
			href = `${AppRoutes.LAWYERAPP}/cases/${caseId}/clients`;
		} else if (!caseRoutesValid.includes(segmentToValidate)) {
			href = `${AppRoutes.LAWYERAPP}/cases/${caseId}/settlement`; // last phase
		}

		router.push(
			{
				pathname: href,
			},
			undefined,
			{ shallow: true }
		);
	};

	const handleHideCase = async () => {
		try {
			await casesLawyerService.hideShowCase(caseId, Boolean(isHideCase));
			queryClient.refetchQueries({
				queryKey: [GET_LAWYER_CASES_LIST],
			});
		} catch (error) {
			formatErrorMessage((error as ErrorEvent)?.message);
		}
	};

	const isTerminal = isTerminalCaseStatus(step);

	const options: Option[] = isArchived
		? [
				{
					label: isArchivePending
						? 'Moving to My Cases...'
						: 'Move to My Cases',
					name: 'unarchive',
					icon: Icons.folderOpen,
					disabled: isArchivePending,
					onClick: () => {
						if (isArchivePending) return;
						onUnarchive?.(caseId);
					},
				},
		  ]
		: [
				{
					label: isHideCase ? 'Show on Homepage' : 'Hide on Homepage',
					name: 'hide',
					icon: isHideCase ? Icons.eye : Icons.eyeSlash,
					onClick: () => {
						handleHideCase();
					},
				},
				...(isTerminal
					? [
							{
								label: isArchivePending ? 'Archiving...' : 'Archive',
								name: 'archive',
								icon: Icons.folderTwo,
								disabled: isArchivePending,
								onClick: () => {
									if (isArchivePending) return;
									onArchive?.(caseId);
								},
							},
					  ]
					: []),
				...(!isEnded
					? [
							{
								label: hasClients ? 'End Case' : 'Delete',
								name: 'endOrDelete',
								icon: hasClients ? Icons.clipboardClose : Icons.trash,
								disabled: isEndOrDeleteDisabled,
								classNameItemLabel: hasClients
									? 'text-black '
									: 'text-main-red',
								classNameItemIcon: hasClients
									? '!text-primary'
									: '!text-main-red',
								onClick: () => {
									openEndOrDeleteModal();
								},
							} satisfies Option,
					  ]
					: []),
		  ];

	return (
		<>
			<div
				className={clsx(
					'rounded-2xl bg-white border border-[#0D061614] p-6 space-y-12 h-[216px] sm:h-fit',
					{
						'!bg-bg-disabled border border-[#0D061614]':
							isArchived ||
							step === 'Archived' ||
							step === 'Case Resolved' ||
							step === 'Resolved Case' ||
							CLOSED_STATUSES_CASE.includes(step),
						'border-main-red': hasError,
					}
				)}
			>
				<div id="card-header" className="min-h-44 space-y-6 h-full relative">
					<div className="space-y-3">
						<div className="flex items-center justify-between">
							<LabelLawyerState
								status={step}
								statusColor={statusColor}
								isDraft={isDraft}
								showDot={false}
							/>
							<div className="flex items-center gap-1">
								{showPin && (
									<div className="flex items-center gap-5 justify-between lg:justify-start">
										<div>
											<PinButton
												isPinned={isPinned}
												onClick={onClickPin}
												noneClick={undefined}
											/>
										</div>
									</div>
								)}
								<Dropdown
									buttonContent={
										<span className="flex items-center justify-center p-0.5 rounded-lg hover:bg-bg-disabled">
											<Icon
												src={Icons.dotsMenuVertical}
												width={16}
												height={16}
												className="size-4 text-opacity-black"
											/>
										</span>
									}
									showChevronDownIcon={false}
									items={options}
								/>
							</div>
						</div>
						<div className="flex flex-wrap items-center justify-between w-full min-w-0">
							<div className="flex flex-col lg:flex-row lg:items-center gap-2 min-w-0 w-full">
								<Typography
									type={'semibold-16'}
									className="truncate !text-[16px] leading-5 block w-full min-w-0"
								>
									{title}
								</Typography>
								{hasError && <ErrorIcon iconSize={18} />}
								{isHideCase && (
									<Icon
										src={Icons.eyeSlash}
										width={24}
										height={24}
										className="text-primary"
									/>
								)}

								{/* {category && <Badge type={category} />} */}
							</div>
						</div>
						<div className="flex items-center gap-4">
							<div className="flex items-center gap-2">
								<Icon
									src={Icons.profileUsers}
									width={16}
									height={16}
									className="text-opacity-black"
								/>
								<Typography
									type="regular-12"
									className="text-start text-opacity-black"
								>
									{(totalClients ?? 0).toString()}
								</Typography>
							</div>
							{createdAt && (
								<div className="flex items-center gap-2">
									<Icon
										src={Icons.calendar}
										width={16}
										height={16}
										className="text-opacity-black"
									/>
									<Typography
										type="regular-12"
										className="text-start text-opacity-black"
									>
										{format(new Date(createdAt), 'MM-dd-yyyy')}
									</Typography>
								</div>
							)}
							{lawyerTasks > 0 && (
								<div className="flex items-center gap-2">
									<Icon
										src={Icons.infoCircle}
										width={16}
										height={16}
										className="text-main-blue-dark"
									/>
									<Typography type="regular-12" className="text-main-blue-dark">
										{lawyerTasks} {lawyerTasks === 1 ? 'Review' : 'Reviews'}
									</Typography>
								</div>
							)}
						</div>
						{isClosedToNewClients && !isArchived && (
							<div className="mt-3 flex items-center gap-2 w-[166px] h-[26px] rounded-lg py-1 px-2 bg-[#F4F4F4]">
								<Icon
									src={Icons.closeCircle}
									className="text-primary shrink-0 !w-[14px] !h-[14px]"
								/>
								<Typography
									type="regular-12"
									className="text-opacity-black whitespace-nowrap"
								>
									Closed to new clients
								</Typography>
							</div>
						)}
						{isArchived && (
							<div className="mt-3 flex items-center gap-2 w-fit h-[26px] rounded-lg py-1 px-2 bg-[#F4F4F4]">
								<Icon
									src={Icons.folderTwo}
									className="text-primary shrink-0 !w-[14px] !h-[14px]"
								/>
								<Typography
									type="regular-12"
									className="text-opacity-black whitespace-nowrap"
								>
									Archived
								</Typography>
							</div>
						)}
					</div>
					<div className="absolute bottom-0 w-full">
						<div className="w-full flex items-center justify-between">
							<Button
								decoration="line-primary"
								size="small"
								className="3xl:!py-4"
								onClick={handleClick}
							>
								View Case
							</Button>
							<div className="flex items-center gap-2">
								{totalMessages > 0 && (
									<div className="flex items-center gap-2">
										<div className="relative">
											<Icon
												src={Icons.message}
												width={16}
												height={16}
												className="text-opacity-black"
											/>
											<SolidDot
												size="9px"
												className="absolute -top-[2px] -right-[3px]"
												color="bg-main-red"
											/>
										</div>
										<Typography
											type="regular-12"
											className="text-start text-opacity-black"
										>
											{totalMessages} New
										</Typography>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
			<ModalConfirmation
				{...END_OR_DELETE_CASE_MODAL_SHELL}
				title={<EndOrDeleteCaseModalTitle hasClients={hasClients} />}
				avoidClose={isSubmitting}
				onClose={closeEndOrDeleteModal}
			>
				<EndOrDeleteCaseModal
					key={isConfirmOpen ? 'end-or-delete-open' : 'end-or-delete-closed'}
					hasClients={hasClients}
					caseTitle={caseTitle}
					register={registerEndOrDelete}
					onConfirm={handleEndOrDelete}
					onClose={closeEndOrDeleteModal}
					isSubmitting={isSubmitting}
				/>
			</ModalConfirmation>
		</>
	);
};

interface PinButton {
	isPinned: boolean;
	noneClick?: boolean;
	onClick?: () => void;
}

const PinButton: React.FC<PinButton> = ({ isPinned, onClick, noneClick }) => {
	const pinIcon = isPinned ? Icons.pinFulled : Icons.pin;
	return (
		<Button
			onClick={onClick}
			className={clsx('rounded-lg !p-1', {
				'bg-gray-10': !isPinned,
				'bg-purple-35 border-transparent': isPinned,
				'cursor-default': noneClick,
			})}
		>
			<Icon
				src={pinIcon}
				width={16}
				height={16}
				className={clsx('text-opacity-black', {
					'text-primary': isPinned,
				})}
			/>
		</Button>
	);
};
